import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, ExternalLink, Eye, EyeOff, LoaderCircle, Lock, PlayCircle, X } from "lucide-react";
import { fileUrl } from "@/lib/api";
import {
  listVerifiedClientGalleryPhotos,
  normalizeClientGalleryCoverFrame,
  normalizeClientGalleryImages,
  verifySecureClientGallery,
} from "@/lib/clientGallerySecureApi";

const emptyForm = { galleryCode: "", password: "" };
const PHOTO_BATCH_SIZE = 60;

function driveFileDownloadUrl(photo) {
  if (photo?.downloadUrl) return photo.downloadUrl;
  if (photo?.driveFileId) {
    return `https://drive.google.com/uc?export=download&id=${encodeURIComponent(photo.driveFileId)}`;
  }
  return photo?.originalUrl || photo?.imageUrl || "";
}

function driveFolderUrl(gallery) {
  if (gallery?.driveFolderUrl) return gallery.driveFolderUrl;
  if (gallery?.driveFolderId) {
    return `https://drive.google.com/drive/folders/${encodeURIComponent(gallery.driveFolderId)}`;
  }
  return "";
}

function isVideoMedia(photo) {
  return photo?.mediaType === "video" || String(photo?.mimeType || "").startsWith("video/");
}

function driveVideoPreviewUrl(photo) {
  if (photo?.previewUrl && /\/preview(?:$|\?)/i.test(photo.previewUrl)) return photo.previewUrl;
  if (photo?.driveFileId) {
    return `https://drive.google.com/file/d/${encodeURIComponent(photo.driveFileId)}/preview`;
  }
  return photo?.previewUrl || photo?.originalUrl || "";
}

export default function ClientGalleries() {
  const { shareToken } = useParams();
  const [form, setForm] = useState(emptyForm);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const [drivePhotos, setDrivePhotos] = useState([]);
  const [drivePhotoTotal, setDrivePhotoTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [accessPassword, setAccessPassword] = useState("");
  const [selectedAlbumId, setSelectedAlbumId] = useState(null);

  const photos = useMemo(() => {
    const imported = drivePhotos.map((photo) => ({ ...photo, source: "drive" }));
    const importedUrls = new Set(imported.flatMap((photo) => [photo.thumbnailUrl, photo.previewUrl, photo.originalUrl]));
    const manual = selectedAlbumId ? [] : normalizeClientGalleryImages(gallery?.gallery)
      .filter((url) => !importedUrls.has(url))
      .map((url, index) => ({
        id: `manual-${index}-${url}`,
        fileName: `Photo ${index + 1}`,
        thumbnailUrl: url,
        previewUrl: url,
        originalUrl: url,
        mediaType: "image",
        mimeType: "image/jpeg",
        source: "manual",
      }));
    return [...imported, ...manual];
  }, [drivePhotos, gallery, selectedAlbumId]);

  const cover = gallery?.coverImage || photos[0]?.previewUrl || photos[0]?.thumbnailUrl || "";
  const photoCount = gallery?.photoCount || drivePhotoTotal || photos.length;
  const hasMore = drivePhotos.length < drivePhotoTotal;
  const isTokenAccess = Boolean(shareToken);
  const albums = useMemo(
    () => (gallery?.albums || []).filter((album) => album.isActive !== false).sort((a, b) => a.sortOrder - b.sortOrder),
    [gallery]
  );
  const selectedAlbum = albums.find((album) => album.id === selectedAlbumId) || null;
  const sourceFolderUrl = driveFolderUrl(selectedAlbum || gallery);
  const coverFrame = useMemo(() => normalizeClientGalleryCoverFrame(gallery), [gallery]);
  const heroFrameStyle = {
    "--client-cover-x": `${coverFrame.coverPositionX}%`,
    "--client-cover-y": `${coverFrame.coverPositionY}%`,
    "--client-cover-scale": coverFrame.coverScale,
  };

  const loadDrivePhotos = async (verifiedGallery, password, offset = 0, albumId = null) => {
    setLoadingMore(true);
    setPhotoError("");
    try {
      const page = await listVerifiedClientGalleryPhotos({
        eventId: verifiedGallery.id,
        shareToken: verifiedGallery.shareToken,
        password,
        albumId,
        limit: PHOTO_BATCH_SIZE,
        offset,
      });
      setDrivePhotoTotal(page.total);
      setDrivePhotos((current) => offset === 0 ? page.photos : [...current, ...page.photos]);
    } catch (err) {
      setPhotoError(err?.message || "Could not load gallery photos.");
    } finally {
      setLoadingMore(false);
    }
  };

  const selectAlbum = async (albumId) => {
    if (albumId === selectedAlbumId || loadingMore) return;
    setSelectedAlbumId(albumId);
    setDrivePhotos([]);
    setDrivePhotoTotal(0);
    setLightbox(null);
    await loadDrivePhotos(gallery, accessPassword, 0, albumId);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const password = form.password;
      const result = await verifySecureClientGallery({
        galleryCode: isTokenAccess ? "" : form.galleryCode,
        password,
        shareToken,
      });
      const firstAlbumId = (result.albums || [])
        .filter((album) => album.isActive !== false)
        .sort((a, b) => a.sortOrder - b.sortOrder)[0]?.id || null;
      setDrivePhotos([]);
      setDrivePhotoTotal(0);
      setAccessPassword(password);
      setSelectedAlbumId(firstAlbumId);
      setGallery(result);
      await loadDrivePhotos(result, password, 0, firstAlbumId);
      setForm(emptyForm);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const message = err.message === "Gallery expired." || err.message === "Gallery inactive."
        ? err.message
        : "Invalid gallery name/code or password.";
      setError(message);
      setGallery(null);
      setDrivePhotos([]);
      setDrivePhotoTotal(0);
    } finally {
      setLoading(false);
    }
  };

  if (gallery) {
    return (
      <div className="min-h-screen bg-black text-[#F7F1E8]">
        <section className="client-secure-gallery-hero relative flex items-end overflow-hidden pt-32 md:pt-36" style={heroFrameStyle}>
          {cover ? (
            <img
              src={fileUrl(cover)}
              alt=""
              className="client-secure-gallery-cover absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[#050505]" />
          )}
          <div className="client-secure-gallery-shade absolute inset-0" />
          <div className="relative z-10 w-full max-w-6xl mx-auto px-6 pb-14">
            <div className="eyebrow text-[#C99A45]">Private Client Gallery</div>
            <h1 className="font-display text-5xl md:text-7xl mt-4">{gallery.title}</h1>
            <div className="mt-6 flex flex-wrap gap-3 text-xs text-[#F7F1E8]/65">
              <span className="border border-[#C99A45]/25 px-3 py-2">{photoCount} media file{photoCount === 1 ? "" : "s"}</span>
              <span className="border border-[#C99A45]/25 px-3 py-2">
                Downloads {gallery.downloadAllowed ? "allowed" : "disabled"}
              </span>
            </div>
          </div>
        </section>

        <main className="max-w-7xl mx-auto px-4 md:px-8 py-14">
          {albums.length > 0 && (
            <div className="mb-8 border-b border-[#C99A45]/20 overflow-x-auto">
              <div className="flex min-w-max gap-1" role="tablist" aria-label="Gallery albums">
                {albums.map((album) => (
                  <button
                    key={album.id}
                    type="button"
                    role="tab"
                    aria-selected={selectedAlbumId === album.id}
                    onClick={() => selectAlbum(album.id)}
                    className={`px-5 py-4 text-[10px] uppercase tracking-[0.24em] border-b-2 transition ${
                      selectedAlbumId === album.id ? "border-[#C99A45] text-[#C99A45]" : "border-transparent text-[#F7F1E8]/55 hover:text-white"
                    }`}
                  >
                    {album.title} <span className="opacity-55">({album.photoCount})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="eyebrow text-[#C99A45]">Album</div>
              <h2 className="font-display text-3xl mt-2">{selectedAlbum?.title || gallery.title}</h2>
            </div>
            {gallery.downloadAllowed && selectedAlbum && sourceFolderUrl && (
              <a href={sourceFolderUrl} target="_blank" rel="noopener noreferrer" className="btn-gold-fill inline-flex items-center gap-2 text-xs">
                <ExternalLink size={13} /> Open {selectedAlbum.title} Folder
              </a>
            )}
          </div>

          {loadingMore && !photos.length ? (
            <div className="border border-[#C99A45]/15 p-8 text-center text-[#C99A45] flex items-center justify-center gap-2">
              <LoaderCircle size={17} className="animate-spin" /> Loading photos...
            </div>
          ) : photos.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {photos.map((photo, index) => {
                const downloadUrl = driveFileDownloadUrl(photo);
                const isVideo = isVideoMedia(photo);
                return (
                  <figure
                    key={photo.id || `${photo.thumbnailUrl}-${index}`}
                    className="border border-[#C99A45]/10 bg-[#050505] group overflow-hidden relative transition"
                  >
                    <button type="button" onClick={() => setLightbox(index)} className="relative block w-full text-left">
                      {photo.thumbnailUrl ? (
                        <img
                          src={fileUrl(photo.thumbnailUrl)}
                          alt={`${gallery.title} ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-auto block transition duration-500 group-hover:opacity-90"
                        />
                      ) : (
                        <div className="aspect-video w-full bg-[#101010] flex items-center justify-center text-[#C99A45]/70">
                          <PlayCircle size={44} />
                        </div>
                      )}
                      {isVideo && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="grid h-16 w-16 place-items-center rounded-full border border-[#C99A45]/45 bg-black/50 text-[#F7F1E8] shadow-2xl backdrop-blur-sm">
                            <PlayCircle size={34} className="ml-0.5" />
                          </span>
                        </div>
                      )}
                    </button>
                    <figcaption className="flex items-center justify-between px-3 py-3 text-[10px] uppercase tracking-[0.22em] text-[#F7F1E8]/45">
                      <span>{isVideo ? "Video" : "Photo"} {index + 1}</span>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => setLightbox(index)} className="hover:text-[#C99A45]" aria-label={`Preview ${isVideo ? "video" : "photo"}`}>
                          <Eye size={13} />
                        </button>
                        {gallery.downloadAllowed && downloadUrl && (
                          <a
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#C99A45]"
                            aria-label={`Download ${isVideo ? "video" : "photo"} from Google Drive`}
                          >
                            <Download size={13} />
                          </a>
                        )}
                      </div>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          ) : (
            <div className="border border-[#C99A45]/15 p-8 text-center text-[#F7F1E8]/55">
              No media available yet.
            </div>
          )}

          {photoError && <div className="mt-6 border border-red-500/25 p-4 text-center text-sm text-red-200">{photoError}</div>}
          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => loadDrivePhotos(gallery, accessPassword, drivePhotos.length, selectedAlbumId)}
                disabled={loadingMore}
                className="btn-gold-outline flex items-center gap-2"
              >
                {loadingMore && <LoaderCircle size={16} className="animate-spin" />}
                {loadingMore ? "Loading photos..." : `Load more photos (${drivePhotos.length} of ${drivePhotoTotal})`}
              </button>
            </div>
          )}
        </main>

        {lightbox !== null && photos[lightbox] && (
          <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <button type="button" onClick={() => setLightbox(null)} className="absolute right-5 top-5 text-[#F7F1E8]/75 hover:text-[#C99A45]" aria-label="Close preview">
              <X size={28} />
            </button>
            <button
              type="button"
              onClick={() => setLightbox((value) => Math.max(0, value - 1))}
              disabled={lightbox === 0}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 btn-gold-outline disabled:opacity-30"
            >
              Prev
            </button>
            {isVideoMedia(photos[lightbox]) ? (
              <div className="w-[92vw] max-w-6xl aspect-video border border-[#C99A45]/25 bg-black shadow-2xl">
                <iframe
                  src={fileUrl(driveVideoPreviewUrl(photos[lightbox]))}
                  title={photos[lightbox].fileName || "Video preview"}
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            ) : (
              <img
                src={fileUrl(photos[lightbox].previewUrl || photos[lightbox].originalUrl || photos[lightbox].thumbnailUrl)}
                alt={photos[lightbox].fileName || ""}
                className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain"
              />
            )}
            <button
              type="button"
              onClick={() => setLightbox((value) => Math.min(photos.length - 1, value + 1))}
              disabled={lightbox === photos.length - 1}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 btn-gold-outline disabled:opacity-30"
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-[#F7F1E8] pt-32 px-5 pb-20 flex items-center justify-center">
      <div className="w-full max-w-5xl grid lg:grid-cols-[0.95fr_1.05fr] border border-[#C99A45]/20 bg-[#050505]">
        <div className="relative min-h-[420px] p-8 md:p-10 flex flex-col justify-between overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(201,154,69,0.18),transparent_32%),linear-gradient(135deg,#0b0b0b,#000)]" />
          <div className="absolute inset-0 border-r border-[#C99A45]/10" />
          <div className="relative">
            <div className="eyebrow text-[#C99A45]">Private Access</div>
            <h1 className="font-display text-5xl md:text-6xl leading-none mt-5">Client Gallery</h1>
            <p className="text-[#F7F1E8]/60 mt-5 max-w-sm">
              Enter the private gallery code and password shared by Mangalfera Films. Albums are never listed publicly.
            </p>
          </div>
          <div className="relative flex items-center gap-3 text-xs text-[#F7F1E8]/55">
            <Lock size={14} className="text-[#C99A45]" />
            Secure private wedding collection
          </div>
        </div>

        <form onSubmit={submit} className="p-8 md:p-10 space-y-5">
          <div>
            <div className="eyebrow text-[#C99A45]">{isTokenAccess ? "Private Link" : "Name + Password"}</div>
            <h2 className="font-display text-4xl mt-3">Open Your Gallery</h2>
          </div>

          {!isTokenAccess && (
            <label className="block">
              <span className="text-xs uppercase tracking-[0.22em] text-[#F7F1E8]/50">Gallery name/code</span>
              <input
                value={form.galleryCode}
                onChange={(event) => setForm({ ...form, galleryCode: event.target.value })}
                className="admin-input w-full mt-2"
                placeholder="example: milly-sagar"
                autoComplete="off"
                required
              />
            </label>
          )}

          <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-[#F7F1E8]/50">Password/PIN</span>
            <div className="relative mt-2">
              <input
                value={form.password}
                onChange={(event) => setForm({ ...form, password: event.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 8) })}
                className="admin-input w-full pr-12"
                placeholder="Enter private PIN"
                type={showPin ? "text" : "password"}
                minLength={4}
                maxLength={8}
                autoComplete="off"
                required
              />
              <button type="button" onClick={() => setShowPin((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F7F1E8]/55 hover:text-[#C99A45]" aria-label={showPin ? "Hide PIN" : "Show PIN"}>
                {showPin ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </label>

          {error && <div className="border border-red-500/30 text-red-200 p-3 text-sm">{error}</div>}

          <button disabled={loading} className="btn-gold-fill w-full flex items-center justify-center gap-2">
            {loading ? <LoaderCircle className="animate-spin" size={17} /> : <Lock size={16} />}
            {loading ? "Loading access..." : "Open Gallery"}
          </button>
        </form>
      </div>
    </div>
  );
}
